import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import {
  House, Truck, MapPin, ClockCounterClockwise, SignOut, PlayCircle, CreditCard, UsersThree, Pulse, ChartBar, Gift
} from "@phosphor-icons/react";
import TrialBanner, { useBilling } from "@/components/TrialBanner";
import InstallPrompt from "@/components/InstallPrompt";
import BottomTabs from "@/components/BottomTabs";

const items = [
  { to: "/app", label: "Dashboard", icon: House, end: true, tid: "nav-dashboard" },
  { to: "/app/loads", label: "Loads", icon: Truck, tid: "nav-loads" },
  { to: "/app/locations", label: "Locations", icon: MapPin, tid: "nav-locations" },
  { to: "/app/history", label: "History", icon: ClockCounterClockwise, tid: "nav-history" },
  { to: "/app/training", label: "Training", icon: PlayCircle, tid: "nav-training" },
  { to: "/app/billing", label: "Plan", icon: CreditCard, tid: "nav-billing" },
  { to: "/app/fleet", label: "Fleet", icon: UsersThree, tid: "nav-fleet" },
  { to: "/app/activity", label: "Activity", icon: Pulse, tid: "nav-activity", ownerOnly: true },
  { to: "/app/reports", label: "Reports", icon: ChartBar, tid: "nav-reports", ownerOnly: true },
  { to: "/app/referrals", label: "Refer", icon: Gift, tid: "nav-referrals", ownerOnly: true },
];

export default function AppShell({ children, focus = false }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const billingState = useBilling();
  const onLogout = async () => { await logout(); nav("/login"); };
  const navItems = items.filter(it => !it.ownerOnly || user?.fleet_role === "owner");

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 bg-primary text-primary-foreground border-b-4 border-accent">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 lg:px-6 h-14">
          <Link to="/app" className="flex items-center gap-2 font-display font-black tracking-tight text-lg" data-testid="brand-link">
            <span className="inline-block w-2 h-6 bg-accent" aria-hidden />
            HAULGUARD
          </Link>
          <nav className="hidden lg:flex items-center gap-0.5 mx-4">
            {navItems.map(it => (
              <NavLink key={it.to} to={it.to} end={it.end} data-testid={it.tid}
                className={({isActive}) =>
                  `px-2 xl:px-3 py-1.5 text-xs xl:text-sm uppercase font-bold tracking-wide ${isActive ? 'bg-accent text-accent-foreground' : 'hover:bg-white/10'}`
                }>
                {it.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3 shrink-0">
            <span className="hidden sm:block lg:hidden 2xl:block text-xs opacity-80 font-mono-tech" data-testid="header-user">{user?.email}</span>
            <button onClick={onLogout} data-testid="btn-logout" className="hidden lg:inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider hover:text-accent">
              <SignOut size={16} /> Logout
            </button>
          </div>
        </div>
      </header>
      <TrialBanner state={billingState}/>
      <main className={`max-w-7xl mx-auto px-4 lg:px-6 py-6 ${focus ? "pb-28" : "pb-24"} lg:pb-6`}>{children}</main>
      <footer className={`border-t border-border mt-8 py-6 text-center text-xs text-muted-foreground ${focus ? "hidden lg:block" : ""}`}>
        <span className="eyebrow">Every vehicle. Accounted for.</span>
      </footer>
      {!focus && <BottomTabs items={navItems} onLogout={onLogout}/>}
      {!focus && <InstallPrompt/>}
    </div>
  );
}
